import {
    useArrowNavigationGroup,
    useFocusVisible,
    useMergedRefs,
    type PositioningVirtualElement,
} from '@fluentui/react-components';
import { observer } from 'owa-mobx-react';
import { isHosted } from 'owa-config/lib/isHosted';
import { isGlobalFeatureEnabled } from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import React from 'react';
import { useState } from 'owa-react';
import { addWrappedEventListener, addDocumentEventListener } from 'owa-event-listener';
import {
    LazyStatusBarReminders,
    LazyStatusBarViewShortcuts,
    LazyStatusBarZoom,
} from '../lazyStatusBarItems';
import { LazyStatusBarContextMenu } from '../lazyStatusBarContextMenu';
import { statusBar, statusBarViewControls } from './StatusBar.scss';
import { useStatusBarCustomization } from './StatusBarCustomizationContext';
import loc from 'owa-localize';
import { customizeStatusBar } from './StatusBar.locstring.json';

type StatusBarProps = {
    targetRef?: React.Ref<HTMLDivElement>;
};

const StatusBar: React.FunctionComponent<StatusBarProps> = observer(function StatusBar({
    targetRef,
}) {
    // Exclude hosted experiences; otherwise require the general flight or Win32 styling and its flight.
    if (
        isHosted() ||
        (!isGlobalFeatureEnabled('mon-status-bar') &&
            (!isWin32StylingEnabled() || !isGlobalFeatureEnabled('mon-win32-styling-status-bar')))
    ) {
        return null;
    }

    return <StatusBarContent targetRef={targetRef} />;
}, 'StatusBar');

const StatusBarContent: React.FunctionComponent<StatusBarProps> = observer(
    function StatusBarContent({ targetRef }) {
        const { visibility } = useStatusBarCustomization();
        const barId = React.useId();
        const barRef = React.useRef<HTMLDivElement>(null);
        const menuAnchorRef = React.useRef<HTMLElement | PositioningVirtualElement | null>(null);
        const menuReturnFocusRef = React.useRef<HTMLElement | null>(null);
        const menuPopoverRef = React.useRef<HTMLDivElement>(null);
        // The status bar is outside #mainApp's focus-visible scope, so it needs its own.
        const focusVisibleRef = useFocusVisible<HTMLDivElement>();
        const rootRef = useMergedRefs(targetRef, barRef, focusVisibleRef);
        // Let Left/Right wrap between controls while preserving Tab/Shift+Tab navigation.
        const navigationAttributes = useArrowNavigationGroup({
            axis: 'horizontal',
            circular: true,
            tabbable: true,
        });
        const [menuInvocation, setMenuInvocation] = useState(0);
        const isMenuOpen = menuInvocation !== 0;
        const [isMenuCenteredOnBar, setIsMenuCenteredOnBar] = useState(false);
        const [areRemindersAvailable, setAreRemindersAvailable] = useState(false);
        const [areViewShortcutsAvailable, setAreViewShortcutsAvailable] = useState(false);
        const [areZoomControlsAvailable, setAreZoomControlsAvailable] = useState(false);
        const onRemindersAvailabilityChanged = React.useCallback((isAvailable: boolean) => {
            setAreRemindersAvailable(isAvailable);
        }, []);
        const onViewShortcutsAvailabilityChanged = React.useCallback((isAvailable: boolean) => {
            setAreViewShortcutsAvailable(isAvailable);
        }, []);
        const onZoomControlsAvailabilityChanged = React.useCallback((isAvailable: boolean) => {
            setAreZoomControlsAvailable(isAvailable);
        }, []);
        // Keep a keyboard entry point until a visible, enabled control actually renders.
        const hasTabbableControls =
            (visibility.reminders && areRemindersAvailable) ||
            (visibility.viewShortcuts && areViewShortcutsAvailable) ||
            (visibility.zoomSlider && areZoomControlsAvailable);
        const onDismissMenu = React.useCallback(() => {
            setMenuInvocation(0);
        }, []);
        // Capture the anchor and return focus before the interaction-only bundle loads.
        const openContextMenu = React.useCallback(
            (anchor: HTMLElement | PositioningVirtualElement | null, centerOnBar: boolean) => {
                const focusedElement = barRef.current?.ownerDocument.activeElement;
                menuReturnFocusRef.current =
                    focusedElement instanceof HTMLElement ? focusedElement : null;
                menuAnchorRef.current = anchor;
                setIsMenuCenteredOnBar(centerOnBar);
                setMenuInvocation(previous => previous + 1);
            },
            []
        );
        // Fall back to the bar's center when keyboard focus is not on one of its items.
        const openKeyboardContextMenu = React.useCallback(() => {
            const bar = barRef.current;
            const focusedElement = bar?.ownerDocument.activeElement;
            const hasFocusedItem =
                focusedElement instanceof HTMLElement &&
                focusedElement !== bar &&
                bar?.contains(focusedElement);
            openContextMenu(hasFocusedItem ? focusedElement : bar, !hasFocusedItem);
        }, [openContextMenu]);
        // Anchor pointer-opened menus to the click rather than the full status-bar element.
        const onContextMenu = React.useCallback(
            (event: MouseEvent) => {
                event.preventDefault();
                const { clientX: x, clientY: y } = event;
                // Coordinate-less context-menu events must not replace the keyboard anchor with (0, 0).
                if (x === 0 && y === 0) {
                    openKeyboardContextMenu();
                    return;
                }
                openContextMenu(
                    {
                        getBoundingClientRect: () => ({
                            x,
                            y,
                            top: y,
                            bottom: y,
                            left: x,
                            right: x,
                            width: 0,
                            height: 0,
                        }),
                    },
                    false
                );
            },
            [openContextMenu, openKeyboardContextMenu]
        );
        const onKeyDown = React.useCallback(
            (event: KeyboardEvent) => {
                if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
                    event.preventDefault();
                    openKeyboardContextMenu();
                } else if (event.key === 'Escape' && isMenuOpen) {
                    event.preventDefault();
                    onDismissMenu();
                }
            },
            [openKeyboardContextMenu, isMenuOpen, onDismissMenu]
        );
        // An outside interaction can cancel opening while the menu bundle is still loading.
        const onPendingMenuInteraction = React.useCallback(
            (event: Event) => {
                if (
                    !menuPopoverRef.current &&
                    event.target instanceof Node &&
                    !barRef.current?.contains(event.target)
                ) {
                    onDismissMenu();
                }
            },
            [onDismissMenu]
        );

        React.useEffect(() => {
            const bar = barRef.current;
            if (!bar) {
                return;
            }

            // Native bubbling includes module fields portaled from outside this React subtree.
            const removeContextMenuListener = addWrappedEventListener(
                'StatusBar',
                bar,
                'contextmenu',
                onContextMenu
            );
            const removeKeyDownListener = addWrappedEventListener(
                'StatusBar',
                bar,
                'keydown',
                onKeyDown
            );
            return () => {
                removeContextMenuListener?.();
                removeKeyDownListener?.();
            };
        }, [onContextMenu, onKeyDown]);

        React.useEffect(() => {
            if (!isMenuOpen) {
                return;
            }

            const targetDocument = barRef.current?.ownerDocument;
            const removePointerDownListener = addDocumentEventListener(
                'StatusBar',
                'pointerdown',
                onPendingMenuInteraction,
                targetDocument
            );
            const removeFocusInListener = addDocumentEventListener(
                'StatusBar',
                'focusin',
                onPendingMenuInteraction,
                targetDocument
            );
            return () => {
                removePointerDownListener?.();
                removeFocusInListener?.();
            };
        }, [isMenuOpen, onPendingMenuInteraction]);

        return (
            <>
                <div
                    id={barId}
                    className={statusBar}
                    ref={rootRef}
                    data-app-section="StatusBar"
                    tabIndex={hasTabbableControls ? undefined : 0}
                    role="group"
                    aria-label={loc(customizeStatusBar)}
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen}
                    aria-controls={isMenuOpen ? `${barId}-menu` : undefined}
                    {...navigationAttributes}
                >
                    {visibility.reminders && (
                        <LazyStatusBarReminders
                            onAvailabilityChanged={onRemindersAvailabilityChanged}
                        />
                    )}
                    <div className={statusBarViewControls}>
                        {visibility.viewShortcuts && (
                            <LazyStatusBarViewShortcuts
                                onAvailabilityChanged={onViewShortcutsAvailabilityChanged}
                            />
                        )}
                        {(visibility.zoomSlider || visibility.zoom) && (
                            <LazyStatusBarZoom
                                onAvailabilityChanged={onZoomControlsAvailabilityChanged}
                            />
                        )}
                    </div>
                </div>
                {isMenuOpen && (
                    <LazyStatusBarContextMenu
                        key={menuInvocation}
                        barId={barId}
                        anchorRef={menuAnchorRef}
                        barRef={barRef}
                        returnFocusRef={menuReturnFocusRef}
                        popoverRef={menuPopoverRef}
                        centerOnBar={isMenuCenteredOnBar}
                        onDismiss={onDismissMenu}
                    />
                )}
            </>
        );
    },
    'StatusBarContent'
);

export default StatusBar;
